import { computed, type ComputedRef } from 'vue';
import type { ApplicantId } from '@/types';

type ApplicantLabel = { id: ApplicantId; name: string };

type Inputs = {
  primaryPartner: ComputedRef<ApplicantLabel | null>;
  otherApplicants: ComputedRef<ApplicantLabel[]>;
  authorisedUserId?: ComputedRef<ApplicantId | undefined>;
};

type Flags = {
  isSecondaryDeclaration?: boolean;
  isIncomeExpenseRequest?: boolean;
};

export const formatNamesList = (names: string[]) => {
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

export function useHouseholdMessages({ primaryPartner, otherApplicants, authorisedUserId }: Inputs, flags: Flags = {}) {
  const hasPartner = computed(() => !!primaryPartner.value);
  const hasOthers = computed(() => otherApplicants.value.length > 0);
  const otherNamesText = computed(() => formatNamesList(otherApplicants.value.map((a) => a.name)));

  const incomeMessage = computed(() => {
    if (!hasPartner.value && !hasOthers.value) {
      return 'Please enter your own income.';
    } else if (hasPartner.value && !hasOthers.value) {
      return `Please enter your own income and ${primaryPartner.value?.name}'s income.`;
    } else if (hasPartner.value && hasOthers.value) {
      return `Please enter your own income and ${primaryPartner.value?.name}'s income. We will request this information from ${otherNamesText.value} via email.`;
    } else {
      return `Please enter your own income. We will request this information from ${otherNamesText.value} via email.`;
    }
  });

  const expensesMessage = computed(() => {
    if (flags.isSecondaryDeclaration && hasPartner.value) {
      return `Please review your combined household expenses with ${primaryPartner.value?.name}. If you wish to make any changes, please contact us.`;
    } else if (
      flags.isIncomeExpenseRequest &&
      hasPartner.value &&
      authorisedUserId?.value === primaryPartner.value?.id
    ) {
      return `Please include your combined household expenses including ${primaryPartner.value?.name}.`;
    } else if (hasPartner.value && !hasOthers.value) {
      return `Please include your combined household expenses including ${primaryPartner.value?.name}.`;
    } else if (hasPartner.value && hasOthers.value) {
      return `Please include your combined household expenses including ${primaryPartner.value?.name}. We will request this information from ${otherNamesText.value} via email.`;
    } else if (hasOthers.value) {
      return `Only include your expenses, we will request this information from ${otherNamesText.value} via email.`;
    } else {
      return 'Please include your household expenses.';
    }
  });

  const assetsMessage = computed(() => {
    if (!hasPartner.value && !hasOthers.value) {
      return 'Please include your assets and liabilities.';
    } else if (hasPartner.value && !hasOthers.value) {
      return `Please include your combined assets and liabilities including ${primaryPartner.value?.name}.`;
    } else if (hasPartner.value && hasOthers.value) {
      return `Please include your combined assets and liabilities including ${primaryPartner.value?.name}. We will request this information from ${otherNamesText.value} via email.`;
    } else {
      return `Only include your assets and liabilities, we will request this information from ${otherNamesText.value} via email.`;
    }
  });

  return { incomeMessage, expensesMessage, assetsMessage, formatNamesList };
}
